import { useCallback, useEffect, useRef, useState } from 'react';
import courtyardEvening from '../assets/checkpoint-charly-garden.jpg';
import gardenDay from '../assets/checkpoint-charly-garden01.jpg';
import diningRoom from '../assets/checkpoint-charly-interior01.jpg';
import jazzCorner from '../assets/checkpoint-charly -interior.jpg';
import wallArt from '../assets/wall-decoration.jpg';
import decorDetail from '../assets/wall-decoratuion01.jpg';
import seasoningsBg from '../assets/seasonings-bg.jpg';
import { useTranslations } from '../i18n';

type GalleryItemCopy = {
  title: string;
  description: string;
};

const DEFAULT_ITEMS: GalleryItemCopy[] = [
  {
    title: 'Courtyard evenings',
    description: 'Soft lights warm the garden for alfresco dinners.',
  },
  {
    title: 'Daytime garden table',
    description: 'Sunlit seating surrounded by greenery for relaxed lunches.',
  },
  {
    title: 'Dining room warmth',
    description: 'Dark wood and velvet chairs welcoming guests indoors.',
  },
  {
    title: 'Jazz corner',
    description: 'Instruments and stage lighting ready for nightly performances.',
  },
  {
    title: 'Gallery wall art',
    description: 'Framed pieces and candlelight create an intimate backdrop.',
  },
  {
    title: 'Signature decor',
    description: 'Vintage mirrors and textures add character to every seat.',
  },
];

const GALLERY_PHOTOS = [
  { src: courtyardEvening },
  { src: gardenDay },
  { src: diningRoom },
  { src: jazzCorner },
  { src: wallArt },
  { src: decorDetail },
];

export function GalleryPage() {
  const { t } = useTranslations();
  const heading = t('gallery.heading', 'Moments from the dining room') as string;
  const body = t(
    'gallery.body',
    'Get a taste of the ambiance—table settings, chef plates, and guest experiences.'
  ) as string;

  const localizedItems = (t('gallery.items', DEFAULT_ITEMS) as GalleryItemCopy[]) ?? DEFAULT_ITEMS;

  const tiles = GALLERY_PHOTOS.map((photo, index) => {
    const fallback = DEFAULT_ITEMS[index] ?? DEFAULT_ITEMS[0];
    const localized = localizedItems[index] ?? fallback;

    return {
      ...photo,
      title: localized.title ?? fallback.title,
      description: localized.description ?? fallback.description,
    };
  });

  const totalTiles = tiles.length;
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const closeLightbox = useCallback(() => {
    setActiveIndex(null);
  }, []);

  const goToNext = useCallback(() => {
    if (totalTiles === 0) {
      return;
    }

    setActiveIndex((prev) => {
      if (prev === null) {
        return prev;
      }

      return (prev + 1) % totalTiles;
    });
  }, [totalTiles]);

  const goToPrevious = useCallback(() => {
    if (totalTiles === 0) {
      return;
    }

    setActiveIndex((prev) => {
      if (prev === null) {
        return prev;
      }

      return (prev - 1 + totalTiles) % totalTiles;
    });
  }, [totalTiles]);

  useEffect(() => {
    if (activeIndex === null) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeLightbox();
      }

      if (event.key === 'ArrowRight') {
        goToNext();
      }

      if (event.key === 'ArrowLeft') {
        goToPrevious();
      }
    };

    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    const focusTimeout = window.setTimeout(() => {
      closeButtonRef.current?.focus();
    }, 0);

    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKeyDown);
      window.clearTimeout(focusTimeout);
    };
  }, [activeIndex, closeLightbox, goToNext, goToPrevious]);

  const activeLightbox = activeIndex !== null ? { tile: tiles[activeIndex], index: activeIndex } : null;

  return (
    <section className="relative isolate overflow-hidden px-6 pb-10 pt-16 sm:px-10 sm:pb-12">
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <img
          src={seasoningsBg}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(12,16,20,0.9)_0%,rgba(12,16,20,0.88)_100%)]" aria-hidden="true" />
      </div>
      <div className="mx-auto flex max-w-6xl flex-col gap-10">
      <header className="space-y-4 text-center">
        <h1 className="text-4xl font-heading text-[var(--color-text-primary)]">{heading}</h1>
        <p className="mx-auto max-w-3xl text-base text-[var(--color-text-primary)]/75">{body}</p>
      </header>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {tiles.map((tile, index) => (
          <button
            key={`${tile.title}-${index}`}
            type="button"
            onClick={() => setActiveIndex(index)}
            className="group flex flex-col gap-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-evergreen"
          >
            <div className="overflow-hidden rounded-3xl shadow-[0_26px_72px_-40px_rgba(0,0,0,0.85)] transition-transform duration-200 group-hover:scale-[1.02] group-focus-visible:scale-[1.02]">
              <img
                src={tile.src}
                alt={tile.title}
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
            </div>
            <div className="space-y-1">
              <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">{tile.title}</h2>
              <p className="text-sm text-[var(--color-text-primary)]/70">{tile.description}</p>
            </div>
          </button>
        ))}
      </div>

      {activeLightbox ? (
        <div
          className="fixed inset-0 z-[1500] flex items-start justify-center bg-black/80 px-6 py-20 md:items-center md:py-12"
          onClick={closeLightbox}
        >
          {totalTiles > 1 ? (
            <>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  goToPrevious();
                }}
                aria-label="View previous photo"
                className="absolute left-8 top-1/2 z-[1505] flex h-14 w-14 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/60 text-white transition-colors duration-150 hover:border-[var(--color-accent)] hover:bg-[var(--color-accent)] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)] focus-visible:border-[var(--color-accent)] focus-visible:bg-[var(--color-accent)] focus-visible:text-white"
              >
                <svg
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="h-6 w-6"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.25 19.25 8.75 12l6.5-7.25" />
                </svg>
              </button>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  goToNext();
                }}
                aria-label="View next photo"
                className="absolute right-8 top-1/2 z-[1505] flex h-14 w-14 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-black/60 text-white transition-colors duration-150 hover:border-[var(--color-accent)] hover:bg-[var(--color-accent)] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)] focus-visible:border-[var(--color-accent)] focus-visible:bg-[var(--color-accent)] focus-visible:text-white"
              >
                <svg
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="h-6 w-6"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="m8.75 4.75 6.5 7.25-6.5 7.25" />
                </svg>
              </button>
            </>
          ) : null}

          <div
            role="dialog"
            aria-modal="true"
            aria-label={activeLightbox.tile.title}
            className="relative w-full max-w-4xl space-y-4"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => {
                closeLightbox();
              }}
              ref={closeButtonRef}
              aria-label={t('gallery.close', 'Close gallery view') as string}
              className="absolute right-4 top-4 z-[10] flex h-12 w-12 items-center justify-center rounded-full border border-white/25 bg-black/60 text-3xl font-semibold leading-none text-white transition-colors duration-150 hover:border-[var(--color-accent)] hover:bg-[var(--color-accent)] hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)] focus-visible:border-[var(--color-accent)] focus-visible:bg-[var(--color-accent)] focus-visible:text-white"
            >
              <span className="pointer-events-none -translate-y-[1px] transform">×</span>
            </button>
            <div className="overflow-hidden rounded-[32px] bg-black/50 p-4 shadow-[0_40px_140px_-60px_rgba(0,0,0,0.9)]">
              <img
                src={activeLightbox.tile.src}
                alt={activeLightbox.tile.title}
                loading="eager"
                className="mx-auto max-h-[60vh] w-full rounded-2xl object-contain"
              />
            </div>
            <div className="space-y-2 text-center text-white">
              <p className="text-base font-semibold text-white">{activeLightbox.tile.title}</p>
              <p className="text-sm text-white/70">{activeLightbox.tile.description}</p>
              <p className="text-xs uppercase tracking-[0.25em] text-white/50">
                {activeLightbox.index + 1} / {totalTiles}
              </p>
            </div>
          </div>
        </div>
      ) : null}
      </div>
    </section>
  );
}
